import { CellContext } from '@tanstack/react-table';
import {
  formatFloatNumber,
  formatDate,
  formatRelativeDate,
} from '@UIComponents/utils/formatters';
import ClipboardCopyLabel from '@UIComponents/ClipboardCopyLabel/ClipboardCopyLabel';
import UIIcon from 'components/UI/Icon/Icon';
import type { ExtendedViewership } from '../types';

interface ImpressionCellProps {
  readonly info?: CellContext<ExtendedViewership, unknown>;
}

function ImpressionCellComponent({ info }: ImpressionCellProps): JSX.Element {
  const { column, row } = info ?? {};
  const value = info?.getValue() as number;
  const formatted = formatFloatNumber(value ?? 0);

  // Extract reporting window from column ID (premiereImpressions -> premiere)
  const reportingWindow = column?.id.replace('Impressions', '');

  // Check if we have healthMetadata for this reporting window
  const healthMetadata = row?.original.healthMetadata?.find(
    (metadata) => metadata.reportingWindow === reportingWindow,
  );

  // If data is upcoming, return a special value with the expected date
  if (
    !value &&
    healthMetadata?.state === 'UPCOMING' &&
    healthMetadata.expectedAvailableAt
  ) {
    const availableDate = new Date(healthMetadata.expectedAvailableAt);
    const absoluteDate = formatDate(availableDate.toISOString());
    const relativeTime = formatRelativeDate(availableDate);

    return (
      <UIIcon
        type="schedule"
        size="small"
        tooltip={`Data will be available after ${absoluteDate} (${relativeTime})`}
      />
    );
  }

  return (
    <ClipboardCopyLabel
      label={formatted ?? '--'}
      copyValue={value?.toString()}
    />
  );
}

export function getImpressionCell(
  info: CellContext<ExtendedViewership, unknown>,
) {
  return <ImpressionCellComponent info={info} />;
}
